import React, { useEffect, useRef } from "react";

// KPMG palette accents for nodes
const COLORS = ["#00B8F5", "#76D2FF", "#00C0AE", "#7213EA", "#FD349C"];

// Slow, connected particle network. Pure canvas, no deps.
// Pauses when offscreen and respects prefers-reduced-motion.
function ParticleNetwork({ density = 0.00009, linkDistance = 140 }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const rafRef = useRef(0);
  const particlesRef = useRef([]);
  const isVisibleRef = useRef(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext("2d");
    const reduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    function resize() {
      const { width, height } = container.getBoundingClientRect();
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const target = Math.max(40, Math.floor(width * height * density));
      const current = particlesRef.current;
      if (current.length === 0) {
        particlesRef.current = Array.from({ length: target }, () =>
          spawn(width, height)
        );
      } else if (current.length < target) {
        for (let i = current.length; i < target; i++) {
          current.push(spawn(width, height));
        }
      } else if (current.length > target) {
        current.length = target;
      }
    }

    function spawn(w, h) {
      return {
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: 1.4 + Math.random() * 1.6,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
      };
    }

    function tick() {
      const { width, height } = container.getBoundingClientRect();
      ctx.clearRect(0, 0, width, height);

      const ps = particlesRef.current;
      for (let i = 0; i < ps.length; i++) {
        const p = ps[i];
        if (!reduced) {
          p.x += p.vx;
          p.y += p.vy;
          if (p.x < 0 || p.x > width) p.vx *= -1;
          if (p.y < 0 || p.y > height) p.vy *= -1;
        }
        ctx.beginPath();
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.85;
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Connecting lines
      ctx.lineWidth = 1;
      for (let i = 0; i < ps.length; i++) {
        for (let j = i + 1; j < ps.length; j++) {
          const a = ps[i];
          const b = ps[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist2 = dx * dx + dy * dy;
          const max2 = linkDistance * linkDistance;
          if (dist2 < max2) {
            const alpha = (1 - dist2 / max2) * 0.35;
            ctx.globalAlpha = alpha;
            ctx.strokeStyle = a.color;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;

      if (isVisibleRef.current) {
        rafRef.current = requestAnimationFrame(tick);
      }
    }

    function start() {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(tick);
    }

    function stop() {
      cancelAnimationFrame(rafRef.current);
    }

    resize();
    start();

    const onResize = () => resize();
    window.addEventListener("resize", onResize);

    // Pause when offscreen to save CPU
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isVisibleRef.current = entry.isIntersecting;
          if (entry.isIntersecting) start();
          else stop();
        });
      },
      { threshold: 0.01 }
    );
    io.observe(container);

    return () => {
      window.removeEventListener("resize", onResize);
      io.disconnect();
      stop();
    };
  }, [density, linkDistance]);

  return (
    <div ref={containerRef} className="particle-network">
      <canvas ref={canvasRef} aria-hidden="true" />
    </div>
  );
}

export default ParticleNetwork;
